import React, { useState, useEffect, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { MCPConfigTemplate, GeneratorOptions, ConfigValidationResult, EditorState } from '../../types';
import { configGenerator } from '../../services/configGenerator';

interface ConfigEditorProps {
  initialConfig?: MCPConfigTemplate;
  options: GeneratorOptions;
  onConfigChange?: (config: MCPConfigTemplate) => void;
  onValidationChange?: (validation: ConfigValidationResult) => void;
  readonly?: boolean;
  height?: string;
}

export function ConfigEditor({ 
  initialConfig, 
  options, 
  onConfigChange, 
  onValidationChange,
  readonly = false,
  height = '400px' 
}: ConfigEditorProps) {
  const [editorState, setEditorState] = useState<EditorState>({
    content: '',
    language: options.outputFormat === 'yaml' ? 'yaml' : 'json',
    isModified: false,
  });
  
  const [validation, setValidation] = useState<ConfigValidationResult>({
    isValid: true,
    errors: [],
    warnings: [],
    suggestions: [],
  });

  const [isValidating, setIsValidating] = useState(false);

  // Initialize editor content
  useEffect(() => {
    if (initialConfig) {
      const content = options.outputFormat === 'yaml' 
        ? configGenerator.toYAML(initialConfig)
        : configGenerator.toJSON(initialConfig);
      
      setEditorState(prev => ({
        ...prev,
        content,
        isModified: false,
        lastSaved: new Date().toISOString(),
      }));
    }
  }, [initialConfig, options.outputFormat]);

  // Validate configuration with debouncing
  const validateConfig = useCallback(async (content: string) => {
    if (!content.trim()) {
      const emptyValidation = {
        isValid: false,
        errors: ['Configuration cannot be empty'],
        warnings: [],
        suggestions: [],
      };
      setValidation(emptyValidation);
      onValidationChange?.(emptyValidation);
      return;
    }

    setIsValidating(true);
    
    try {
      let config: MCPConfigTemplate;
      
      if (options.outputFormat === 'yaml') {
        // In a real implementation, use a proper YAML parser
        throw new Error('YAML parsing not implemented - use JSON for now');
      } else {
        config = configGenerator.fromJSON(content);
      }

      const validationResult = configGenerator.validateConfig(config);
      setValidation(validationResult);
      onValidationChange?.(validationResult);
      
      if (validationResult.isValid) {
        onConfigChange?.(config);
      }
    } catch (error) {
      const errorValidation = {
        isValid: false,
        errors: [error instanceof Error ? error.message : 'Invalid configuration format'],
        warnings: [],
        suggestions: ['Check JSON syntax and structure'],
      };
      setValidation(errorValidation);
      onValidationChange?.(errorValidation);
    } finally {
      setIsValidating(false);
    }
  }, [options.outputFormat, onConfigChange, onValidationChange]);

  // Debounced validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (editorState.content) {
        validateConfig(editorState.content);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [editorState.content, validateConfig]);

  const handleEditorChange = (value: string | undefined) => {
    if (value !== undefined) {
      setEditorState(prev => ({
        ...prev,
        content: value,
        isModified: true,
      }));
    }
  };

  const formatDocument = () => {
    try {
      if (options.outputFormat === 'json') {
        const config = configGenerator.fromJSON(editorState.content);
        const formatted = configGenerator.toJSON(config, true);
        setEditorState(prev => ({
          ...prev,
          content: formatted,
        }));
      }
    } catch (error) {
      console.error('Failed to format document:', error);
    }
  };

  const resetToInitial = () => {
    if (initialConfig) {
      const content = options.outputFormat === 'yaml' 
        ? configGenerator.toYAML(initialConfig)
        : configGenerator.toJSON(initialConfig);
      
      setEditorState(prev => ({
        ...prev,
        content,
        isModified: false,
      }));
    }
  };

  return (
    <div className="space-y-3">
      {/* Editor Toolbar */}
      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-t-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {editorState.language.toUpperCase()} Configuration
          </span>
          {isValidating && (
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          )}
          {editorState.isModified && (
            <span className="text-xs text-orange-600 dark:text-orange-400">• Modified</span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {!readonly && (
            <>
              <button
                onClick={formatDocument}
                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                title="Format Document"
              >
                Format
              </button>
              {editorState.isModified && (
                <button
                  onClick={resetToInitial}
                  className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                  title="Reset to Initial"
                >
                  Reset
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Monaco Editor */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-b-lg overflow-hidden">
        <Editor
          height={height}
          language={editorState.language}
          value={editorState.content}
          onChange={handleEditorChange}
          options={{
            readOnly: readonly,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 14,
            lineNumbers: 'on',
            wordWrap: 'on',
            automaticLayout: true,
            formatOnPaste: true,
            formatOnType: true,
            tabSize: 2,
            insertSpaces: true,
            detectIndentation: false,
            renderWhitespace: 'selection',
            bracketPairColorization: { enabled: true },
            suggest: {
              showKeywords: true,
              showSnippets: true,
            },
          }}
          theme="vs-dark"
        />
      </div>

      {/* Validation Results */}
      {(validation.errors.length > 0 || validation.warnings.length > 0 || validation.suggestions.length > 0) && (
        <div className="space-y-2">
          {/* Errors */}
          {validation.errors.length > 0 && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                Errors ({validation.errors.length})
              </h4>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                {validation.errors.map((error, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {validation.warnings.length > 0 && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                Warnings ({validation.warnings.length})
              </h4>
              <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                {validation.warnings.map((warning, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-yellow-500 mt-0.5">•</span>
                    <span>{warning}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suggestions */}
          {validation.suggestions.length > 0 && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                Suggestions ({validation.suggestions.length})
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                {validation.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-blue-500 mt-0.5">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Status */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-800 rounded">
        <div className="flex items-center space-x-4">
          <span>
            Status: {validation.isValid ? '✓ Valid' : '✗ Invalid'}
          </span>
          <span>
            Lines: {editorState.content.split('\n').length}
          </span>
          <span>
            Size: {new Blob([editorState.content]).size} bytes
          </span>
        </div>
        {editorState.lastSaved && (
          <span>
            Last saved: {new Date(editorState.lastSaved).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
}